import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { isServiceActive } from '@/utils/isServiceActive';

import { Icon } from '@/components/Icon';
import { LegacyButton } from '@/components/LegacyButton';
import { Modal } from '@/components/Modal';
import { Perk } from '@/components/Perk/Perk';
import type {
  ContentfulPlaceholderProductDetailsFragment,
  ContentfulServicePerk,
} from '@/graphql/contentful/api';
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import type { ServicePerkType } from '@/integrations/contentful';
import { COMPONENT_RENDERER_OPTIONS } from '@/integrations/contentful';
import { generateDataTestId } from '@/lib/tests';
import { commonMessages } from '@/messages/common';
import { useConfigProvider } from '@/providers/ConfigProvider';

import * as S from './ServicePerks.styles';

type ServicePerksProps = S.WrapperProps &
  Pick<ContentfulPlaceholderProductDetailsFragment, 'perksCollection'> & {
    enabledEngraving?: boolean;
  };

export const ServicePerks = ({
  enabledEngraving = false,
  mdHidden,
  mdVisible,
  perksCollection,
  variant,
}: ServicePerksProps) => {
  const { moduleActivations, warrantyExtensionCampaignEnable } =
    useConfigProvider();
  const [openPerk, setOpenPerk] = useState<null | string>(null);
  const wrapperRef = useRef(null);
  const entry = useIntersectionObserver(wrapperRef, {
    threshold: 1,
  });
  const { formatMessage } = useIntl();
  const isVisible = !!entry?.isIntersecting;
  const isDesktop = variant === 'desktop';

  useEffect(() => {
    if (!isVisible && openPerk !== null) {
      setOpenPerk(null);
    }
  }, [isVisible]);

  return (
    <S.Wrapper
      {...generateDataTestId('servicePerks', 'wrapper')}
      ref={wrapperRef}
      data-test-id='ServicePerksWrapper'
      mdHidden={mdHidden}
      mdVisible={mdVisible}
      variant={variant}
    >
      {(perksCollection?.items as ContentfulServicePerk[])
        .filter(({ type }) => {
          if (type === 'engraving') {
            return (
              isServiceActive(moduleActivations, type, true) && enabledEngraving
            );
          }
          if (type === 'warranty') {
            return warrantyExtensionCampaignEnable;
          }

          return isServiceActive(moduleActivations, type, true);
        })
        .map(({ heading, modalText, sys, type, url }, i) => {
          return (
            <S.PerkWrapper
              key={sys.id}
              $nbPerks={perksCollection?.items?.length ?? 0}
              data-test-id={`ServicePerksPerkWrapper-${i}`}
            >
              <Perk
                data-test-id={`ServicePerksPerk-${i}`}
                iconSize='small'
                onClick={() => setOpenPerk(sys.id)}
                type={type as ServicePerkType}
                variant={isDesktop ? 'grey' : 'white'}
              >
                <S.Text data-test-id={`ServicePerksText-${i}`}>
                  {heading}
                </S.Text>
              </Perk>
              <Modal
                data-test-id={`ServicePerksModal-${i}`}
                isOpen={openPerk === sys.id}
                onClose={() => setOpenPerk(null)}
              >
                <Modal.Header onClose={() => setOpenPerk(null)}>
                  {heading}
                </Modal.Header>
                <Modal.Body>
                  {modalText &&
                    documentToReactComponents(
                      modalText.json,
                      COMPONENT_RENDERER_OPTIONS,
                    )}

                  {url && (
                    <LegacyButton
                      data-test-id={`ServicePerksButton-${i}`}
                      isFluid
                      label={formatMessage(commonMessages.findOutMore)}
                      linkProps={{ href: url }}
                      mt={3}
                      variant='link'
                    >
                      <Icon color='primary' name='chevronRight' />
                    </LegacyButton>
                  )}
                </Modal.Body>
              </Modal>
            </S.PerkWrapper>
          );
        })}
    </S.Wrapper>
  );
};

ServicePerks.displayName = 'Perks';
